import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    List,
    ListItemButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { Brand, Category, Product } from "../../types";
import { productService } from "../../services/productService";
import { ProductThumbnail } from "../common/ProductThumbnail";
import { formatCurrencyBDT } from "../../utils/currency";

const PAGE_SIZE = 50;

interface ProductPickerDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the products ticked, in the order they were ticked. */
    onAdd: (products: Product[]) => void;
    /** Already chosen — shown ticked and disabled. */
    excludeIds?: Set<string>;
    title?: string;
    confirmLabel?: (count: number) => string;
}

const available = (p: Product) => (p.stock?.quantity ?? 0) - (p.stock?.reserved_quantity ?? 0);

/**
 * Pick several products at once: search by name or SKU, narrow by category
 * or brand, tick rows (or "Select all shown"), then add them together.
 */
export const ProductPickerDialog: React.FC<ProductPickerDialogProps> = ({
    open,
    onClose,
    onAdd,
    excludeIds,
    title = "Add products",
    confirmLabel = (n) => (n ? `Add ${n} product${n === 1 ? "" : "s"}` : "Add products"),
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [search, setSearch] = useState("");
    const [term, setTerm] = useState("");
    const [category, setCategory] = useState<Category | null>(null);
    const [brand, setBrand] = useState<Brand | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);

    const [results, setResults] = useState<Product[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [picked, setPicked] = useState<Map<string, Product>>(new Map());
    const requestRef = useRef(0);

    // Filters' option lists, once per open.
    useEffect(() => {
        if (!open) return;
        Promise.all([
            productService.getCategories({ limit: 1000, active_only: true }),
            productService.getBrands({ limit: 1000, active_only: true }),
        ])
            .then(([cats, brs]) => {
                setCategories([...cats].sort((a, b) => a.name.localeCompare(b.name)));
                setBrands([...brs].sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => undefined);
    }, [open]);

    // Start fresh each time it opens.
    useEffect(() => {
        if (open) {
            setPicked(new Map());
            setSearch("");
            setTerm("");
        }
    }, [open]);

    // Debounce typing.
    useEffect(() => {
        const t = setTimeout(() => setTerm(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const load = async (skip: number) => {
        const id = ++requestRef.current;
        setLoading(true);
        setError("");
        try {
            const res = await productService.getProducts({
                skip,
                limit: PAGE_SIZE,
                active_only: true,
                search: term || undefined,
                category_id: category?.category_id,
                brand_id: brand?.brand_id,
                sort_by: "name-asc",
                lean: true,
            });
            if (id !== requestRef.current) return;
            setResults((prev) => (skip === 0 ? res.data : [...prev, ...res.data]));
            setTotal(res.total);
        } catch {
            if (id === requestRef.current) setError("Couldn't load products. Try again.");
        } finally {
            if (id === requestRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, term, category, brand]);

    const selectable = useMemo(
        () => results.filter((p) => !excludeIds?.has(p.product_id)),
        [results, excludeIds],
    );
    const allShownPicked = selectable.length > 0 && selectable.every((p) => picked.has(p.product_id));

    const toggle = (p: Product) =>
        setPicked((prev) => {
            const next = new Map(prev);
            if (next.has(p.product_id)) next.delete(p.product_id);
            else next.set(p.product_id, p);
            return next;
        });

    const toggleAllShown = () =>
        setPicked((prev) => {
            const next = new Map(prev);
            if (allShownPicked) selectable.forEach((p) => next.delete(p.product_id));
            else selectable.forEach((p) => next.set(p.product_id, p));
            return next;
        });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: { sm: 480 } }}>
                <TextField
                    autoFocus
                    size="small"
                    placeholder="Search by name or SKU"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <Autocomplete
                        size="small"
                        options={categories}
                        value={category}
                        onChange={(_, v) => setCategory(v)}
                        getOptionLabel={(c) => c.name}
                        isOptionEqualToValue={(a, b) => a.category_id === b.category_id}
                        renderInput={(params) => <TextField {...params} label="Category" />}
                    />
                    <Autocomplete
                        size="small"
                        options={brands}
                        value={brand}
                        onChange={(_, v) => setBrand(v)}
                        getOptionLabel={(b) => b.name}
                        isOptionEqualToValue={(a, b) => a.brand_id === b.brand_id}
                        renderInput={(params) => <TextField {...params} label="Brand" />}
                    />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" color="text.secondary">
                        {loading && results.length === 0 ? "Searching…" : `${total} product${total === 1 ? "" : "s"}`}
                    </Typography>
                    {selectable.length > 0 && (
                        <Button size="small" onClick={toggleAllShown}>
                            {allShownPicked ? "Clear shown" : `Select all ${selectable.length} shown`}
                        </Button>
                    )}
                </Box>

                {error && (
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                )}

                <List dense disablePadding sx={{ flex: 1, overflowY: "auto", mx: -1 }}>
                    {results.map((p) => {
                        const already = !!excludeIds?.has(p.product_id);
                        const checked = already || picked.has(p.product_id);
                        const stock = available(p);
                        const onSale = p.is_on_sale && p.effective_price != null && p.effective_price < p.selling_price;
                        return (
                            <ListItemButton
                                key={p.product_id}
                                onClick={() => !already && toggle(p)}
                                disabled={already}
                                sx={{ borderRadius: 1.5, gap: 1.5, py: 0.75, alignItems: "center" }}
                            >
                                <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple sx={{ p: 0.5 }} />
                                <ProductThumbnail images={p.images} size={40} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap title={p.name}>
                                        {p.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                                        {p.sku} · {stock > 0 ? `${stock} in stock` : "Out of stock"}
                                        {p.live_campaign ? ` · in ${p.live_campaign.name}` : ""}
                                    </Typography>
                                </Box>
                                {already ? (
                                    <Chip size="small" label="Added" />
                                ) : (
                                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                                            {formatCurrencyBDT(onSale ? p.effective_price! : p.selling_price, { ignoreDecimals: true })}
                                        </Typography>
                                        {onSale && (
                                            <Typography variant="caption" color="error.main">
                                                on sale
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </ListItemButton>
                        );
                    })}
                    {results.length < total && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                            <Button size="small" onClick={() => load(results.length)} disabled={loading}>
                                {loading ? <CircularProgress size={16} /> : `Show more (${total - results.length} left)`}
                            </Button>
                        </Box>
                    )}
                    {!loading && results.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 3, textAlign: "center" }}>
                            No products match.
                        </Typography>
                    )}
                </List>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={picked.size === 0}
                    onClick={() => {
                        onAdd(Array.from(picked.values()));
                        onClose();
                    }}
                >
                    {confirmLabel(picked.size)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductPickerDialog;
